import { useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, Flex, Text, Box, Spinner } from '@radix-ui/themes';

import SharedButton from '../../../shared/Button';
import { useFetchBroadcastMessage } from '../hooks/useFetchBroadcastMessages';
import {
  useRetryMutation,
  useCancelScheduledMutation,
  useSendScheduledNowMutation,
  useSendBroadcastMessageMutation,
  useUpdateDraftDateMutation,
} from '../hooks/useBroadcastMessagesMutation';
import { useSmsPreview } from '../hooks/useSmsPreview';

import LoadingComponent from '../../../shared/LoadingComponent';
import { ConfirmationModal } from './ConfirmationModal.jsx';
import { SaveDraftButton } from './SaveDraftButton.jsx';
import { ScheduleModal } from './ScheduleModal.jsx';

import { ERROR, SCHEDULED, DRAFT } from '../utils/constants';
import { navigateBack } from '../utils/navigation';

import { Summary } from './Summary.jsx';
import styles from './SingleBroadcast.module.css';

const TEXT = 'text';
const EMAIL = 'email';

const RetryBtn = ({ id }) => {
  const { t } = useTranslation('broadcasts');
  const { retryMutation, isLoading } = useRetryMutation();

  if (isLoading) {
    return <Spinner size="3" className={styles.spinnerIcon} />;
  }

  return (
    <Button variant="ghost" color="gray" onClick={() => retryMutation({ id })}>
      <i className={`fa-regular fa-rotate-right fa-lg ${styles.retryIcon}`} />
      {t('singleBroadcast.retry')}
    </Button>
  );
};

const ScheduledInfo = ({ scheduledAt }) => {
  return (
    <div className={styles.scheduledInfoWrapper}>
      <i className={`fa-regular fa-clock ${styles.clockIcon}`} />
      <Text size="2" color="gray">
        {scheduledAt}
      </Text>
    </div>
  );
};

const ScheduledActions = ({ id }) => {
  const { t } = useTranslation('broadcasts');
  const {
    sendScheduledNowMutation,
    isSendingNow,
  } = useSendScheduledNowMutation();
  const {
    cancelScheduledMutation,
    isCancelling,
  } = useCancelScheduledMutation();
  const [showSendNowModal, setShowSendNowModal] = useState(false);

  const handleSendNow = () => {
    sendScheduledNowMutation({ id });
    setShowSendNowModal(false);
  };

  if (isSendingNow || isCancelling) {
    return <Spinner size="3" className={styles.spinnerIcon} />;
  }

  return (
    <Flex gap="3" align="center" className={styles.actionButtons}>
      <Button
        variant="outline"
        color="gray"
        size="3"
        onClick={() => cancelScheduledMutation({ id })}
      >
        {t('singleBroadcast.cancelSchedule')}
      </Button>
      <Button
        variant="solid"
        size="3"
        onClick={() => setShowSendNowModal(true)}
      >
        <i className={`fa-regular fa-paper-plane-top ${styles.sendIcon}`} />
        {t('singleBroadcast.sendNow')}
      </Button>
      <ConfirmationModal
        header={t('singleBroadcast.sendNowConfirmHeader')}
        body={t('singleBroadcast.sendNowConfirmBody')}
        cancelText={t('confirmationModal.cancel')}
        submitText={
          <>
            <i className={`fa-regular fa-paper-plane-top ${styles.sendIcon}`} />
            {t('singleBroadcast.sendNow')}
          </>
        }
        open={showSendNowModal}
        onClose={() => setShowSendNowModal(false)}
        onSave={handleSendNow}
      />
    </Flex>
  );
};

const DraftDateInfo = ({ draftDate }) => {
  return (
    <div className={styles.scheduledInfoWrapper}>
      <i className={`fa-regular fa-calendar ${styles.clockIcon}`} />
      <Text size="2" color="gray">
        {draftDate}
      </Text>
    </div>
  );
};

const DraftActions = ({ id, broadcastMessage }) => {
  const { t } = useTranslation('broadcasts');
  const [showChangeDateModal, setShowChangeDateModal] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const {
    updateDraftDateMutation,
    isUpdatingDraftDate,
  } = useUpdateDraftDateMutation();
  const {
    sendBroadcastMessageMutation,
    isSending,
  } = useSendBroadcastMessageMutation();

  const handleChangeDate = selectedDate => {
    const year = selectedDate.getFullYear();
    const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
    const day = String(selectedDate.getDate()).padStart(2, '0');
    updateDraftDateMutation({ id, draftDate: `${year}-${month}-${day}` });
  };

  const handleSchedule = ({ scheduledDate, scheduledHour }) => {
    sendBroadcastMessageMutation({
      uuid: id,
      subject: broadcastMessage.subject,
      smsBody: broadcastMessage.smsBody,
      emailBody: broadcastMessage.emailBody,
      broadcastType:
        broadcastMessage.broadcastType === 'text'
          ? 'sms'
          : broadcastMessage.broadcastType,
      scheduledDate,
      scheduledHour,
    });
  };

  if (isUpdatingDraftDate || isSending) {
    return <Spinner size="3" className={styles.spinnerIcon} />;
  }

  return (
    <Flex gap="3" align="center" className={styles.actionButtons}>
      <Button
        variant="outline"
        color="gray"
        size="3"
        onClick={() => setShowChangeDateModal(true)}
      >
        {t('singleBroadcast.changeDate')}
      </Button>
      <Button
        variant="solid"
        size="3"
        onClick={() => setShowScheduleModal(true)}
      >
        {t('singleBroadcast.scheduleDraft')}
      </Button>
      <SaveDraftButton
        open={showChangeDateModal}
        onClose={() => setShowChangeDateModal(false)}
        onSave={handleChangeDate}
      />
      <ScheduleModal
        open={showScheduleModal}
        onClose={() => setShowScheduleModal(false)}
        onSchedule={handleSchedule}
      />
    </Flex>
  );
};

export const SingleBroadcast = ({ id }) => {
  const { t } = useTranslation('broadcasts');
  const history = useHistory();
  const location = useLocation();
  const { broadcastMessage, isFetching } = useFetchBroadcastMessage({
    id,
  });
  const { previewMessage } = useSmsPreview(broadcastMessage?.smsBody);

  if (isFetching) return <LoadingComponent />;

  const {
    smsAudience,
    emailAudience,
    broadcastType,
    totalAudience,
    sentAt,
    scheduledAt,
    status,
    subject,
    emailBody,
    audiences,
    draftDate,
  } = broadcastMessage;

  const isScheduled = status === SCHEDULED;
  const isDraft = status === DRAFT;

  const convertBroadcastType = () => {
    switch (broadcastType) {
      case TEXT:
        return 'sms';
      case EMAIL:
        return 'email';
      default:
        return 'both';
    }
  };

  // With the uuid, the previews show the real email and offer Download
  // Preview, as Review does for a saved draft.
  const values = {
    uuid: id,
    broadcastType: convertBroadcastType(broadcastType),
    subject,
    smsBody: previewMessage,
    emailBody,
  };

  const renderRightContent = () => {
    if (isScheduled) {
      return <ScheduledInfo scheduledAt={scheduledAt} />;
    }
    if (isDraft && draftDate) {
      return <DraftDateInfo draftDate={draftDate} />;
    }
    if (status !== ERROR && sentAt) {
      return (
        <Text size="2" color="gray">
          {sentAt}
        </Text>
      );
    }
    return null;
  };

  return (
    <Box>
      <div className={styles.header}>
        <SharedButton
          onClick={() => navigateBack(history, location)}
          mode="text"
          variant="black"
        >
          <i className={`fa-solid fa-chevron-left ${styles.backArrow}`} />
          {t('singleBroadcast.broadcastMessage')}
        </SharedButton>
      </div>
      <Box className={styles.content}>
        <Summary
          header={t('singleBroadcast.header')}
          values={values}
          userCount={totalAudience}
          emailCount={emailAudience}
          smsCount={smsAudience}
          rightContent={renderRightContent()}
          status={status}
          audiences={audiences}
        />
      </Box>
      {(isScheduled || isDraft || status === ERROR) && (
        <Flex justify="between" align="center" className={styles.footer}>
          <div />
          {isScheduled ? (
            <ScheduledActions id={id} />
          ) : isDraft ? (
            <DraftActions id={id} broadcastMessage={broadcastMessage} />
          ) : (
            <RetryBtn id={id} />
          )}
        </Flex>
      )}
    </Box>
  );
};
